'use client';

import React from 'react';
import type { NextPage } from "next";
import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import Select, { SingleValue } from "react-select";
import { CaptureError, FetchWithVersionCheck, GetBuildId, recordStat } from '@/shared/functions';
import Image from "next/image";
import { Tooltip } from '@heroui/tooltip';
import { KeyboardIcon, SignOutIcon, ArrowRightIcon, CloudArrowUpIcon, ListDashesIcon, CaretDownIcon, LayoutIcon, CircleDashedIcon, DotsThreeOutlineIcon, MegaphoneIcon, GearIcon, QuestionIcon, FlagBannerFoldIcon, LightningIcon, PlusIcon, TextboxIcon, NewspaperIcon, ShieldCheckIcon, FileMagnifyingGlassIcon, FileCloudIcon, UserCircleIcon, CalendarDotsIcon, FilesIcon } from "@phosphor-icons/react";
import HotkeysModal from './hotkeys-modal';
import { HotKeys } from './hot-keys';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, DropdownSection } from '@heroui/dropdown';
import { SelectStyles } from '../shared/styleDefs';
import { TINY_VIEWPORT_WIDTH } from '../shared/constants';
import { AuthContext, FeaturesContext } from '@/app/contexts';
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/popover';
import { FeatureFlagTypes, View, isWorkProductView, RemoteStorage } from '@/shared/types';
import { StatsContext } from '@/app/contexts';
import Walkthru from './walkThru';
import { useMediaSourceShare } from './mediaSourceShare';
export interface TimelineDropdownItem {
  id: string;
  tags: string;
  title: string;
  description: string;
  createdBy: string;
  createdAt: string;
  eventCount?: number;
}
const FeedbackButton = ({
  onFeedbackClick
}: {
  onFeedbackClick: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  return <div id="FeedbackButton" onClick={onFeedbackClick} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer" data-sentry-component="FeedbackButton" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px] flex items-center justify-center"><MegaphoneIcon mirrored size={22} data-sentry-element="MegaphoneIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      <span className="whitespace-nowrap">Feedback</span>
    </div>;
};
const DocumentIdInput = ({
  featureFlags,
  documentIdInputRef,
  queryKey,
  handleQueryKeyChange,
  handleQueryKeySubmit,
  closeDropdown
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  documentIdInputRef: React.RefObject<HTMLInputElement>;
  queryKey: string;
  handleQueryKeyChange: (queryKey: string) => void;
  handleQueryKeySubmit: (queryKey: string) => void;
  closeDropdown: () => void;
}) => {
  const [queryKeyInput, setQueryKeyInput] = useState(queryKey);
  const statsContext = useContext(StatsContext);
  return <div className="dropdown-item relative" onClick={e => {
    e.stopPropagation();
    e.preventDefault();
  }} data-sentry-component="DocumentIdInput" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <input ref={documentIdInputRef} aria-label="Document ID" name="documentID" placeholder="Enter Doc ID" className="box-border w-[155px] md4:w-[270px] h-[38px] ml-0 mr-[-8px] sm:ml-1 pl-2 sm:pl-4 py-[5.5px] pr-[45px] text-[16px] text-white font-light font-inter bg-white/10 sm:rounded-3xl gap-2 inline-flex resize-none placeholder-white focus:placeholder-transparent focus:outline-none border-white border-opacity-10 border-[1px] border-solid focus:border-white/50 hover:border-white/70" defaultValue={queryKeyInput} onChange={e => handleQueryKeyChange(e.target.value)} onKeyDown={e => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        handleQueryKeySubmit(documentIdInputRef.current?.value ?? '');
        closeDropdown();
      } else {
        setQueryKeyInput(documentIdInputRef.current?.value ?? '');
      }
    }} />
      <button id="SubmitDocumentID" aria-label="Submit Document ID" name="submitDocumentID" type="submit" className="flex absolute right-[5px] pl-[8.5px] md1:pl-[2px] h-[20px] top-1/2 transform -translate-y-1/2 bg-transparent items-center justify-center hover:cursor-pointer text-white outline-none" onClick={event => {
      handleQueryKeySubmit(documentIdInputRef.current?.value ?? '');
      closeDropdown();
      recordStat('SubmitDocumentIDButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
    }}>
        <ArrowRightIcon size={20} className="pr-[1px] sm:pr-[6px]" data-sentry-element="ArrowRightIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" />
      </button>
    </div>;
};
const Sync = ({
  featureFlags,
  handleSyncData,
  isSyncing,
  overflow
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  handleSyncData: () => void;
  isSyncing: boolean;
  overflow: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  if (overflow) {
    return <div id="SyncDataButton" role="button" tabIndex={0} aria-label={isSyncing ? "Syncing data" : "Sync data"} onClick={event => {
      handleSyncData();
      recordStat('SyncDataButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
    }} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleSyncData();
        recordStat('SyncDataButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
      }
    }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer">
        <div className="w-[22px] h-[22px] flex items-center justify-center"><CloudArrowUpIcon size={22} /></div>
        <span className="whitespace-nowrap">{isSyncing ? "Syncing..." : "Sync Data"}</span>
      </div>;
  }
  return <button id="SyncDataButton" onClick={event => {
    handleSyncData();
    recordStat('SyncDataButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
  }} disabled={isSyncing} className="sm:flex hidden icon-text-btn dropdown-item" data-sentry-component="Sync" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px]"><CloudArrowUpIcon size={22} data-sentry-element="CloudArrowUpIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      <span className={`whitespace-nowrap ${overflow ? '' : 'hidden md5:inline'}`}>{isSyncing ? "Syncing..." : "Sync Data"}</span>
    </button>;
};
const Logout = ({
  featureFlags,
  handleLogout,
  overflow
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  handleLogout: () => void;
  overflow: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  if (overflow) {
    return <div id="LogoutButton" role="button" tabIndex={0} aria-label="Log out" onClick={event => {
      handleLogout();
      recordStat('LogoutButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
    }} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleLogout();
        recordStat('LogoutButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
      }
    }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer">
        <div className="w-[22px] h-[22px] flex items-center justify-center"><SignOutIcon size={22} /></div>
        <span className="whitespace-nowrap">Log Out</span>
      </div>;
  }
  return <button id="LogoutButton" className="icon-text-btn dropdown-item" onClick={event => {
    handleLogout();
    recordStat('LogoutButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
  }} data-sentry-component="Logout" data-sentry-source-file="navigation-bar-horizontal.tsx">
    <div className="w-[22px] h-[22px]"><SignOutIcon size={22} data-sentry-element="SignOutIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
    <span className={`whitespace-nowrap ${overflow ? '' : 'hidden md6:inline'}`}>Log Out</span>
  </button>;
};
const Hotkeys = ({
  setShowHotkeysModal,
  overflow,
  featureFlags
}: {
  setShowHotkeysModal: (show: boolean) => void;
  overflow: boolean;
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
}) => {
  const statsContext = useContext(StatsContext);
  if (overflow) {
    return <div id="HotkeysButton" role="button" tabIndex={0} aria-label="Show keyboard shortcuts" onClick={event => {
      setShowHotkeysModal(true);
      recordStat('OpenHotkeysButtonClicked', event, statsContext, !!featureFlags?.useIntercom as boolean);
    }} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowHotkeysModal(true);
        recordStat('OpenHotkeysButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
      }
    }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer">
        <div className="w-[22px] h-[22px] flex items-center justify-center"><KeyboardIcon size={22} /></div>
        <span className="whitespace-nowrap">Hotkeys</span>
      </div>;
  }
  return <div className="hidden md1:flex md1:flex-row md1:items-center" data-sentry-component="Hotkeys" data-sentry-source-file="navigation-bar-horizontal.tsx">
      {!overflow && <div className="divider"></div>}
      <button id="HotkeysButton" aria-label="Show keyboard shortcuts" onClick={event => {
      setShowHotkeysModal(true);
      recordStat('OpenHotkeysButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
    }} className="icon-text-btn">
        <div className="w-[22px] h-[22px]"><KeyboardIcon size={22} data-sentry-element="KeyboardIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      </button>
    </div>;
};
const Chat = ({
  featureFlags,
  overflow,
  loadingTimeline,
  legacyTimelineIsAvailable,
  newTimelineIsAvailable,
  timelinesList,
  selectedView,
  handleViewChange
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  overflow: boolean;
  loadingTimeline: boolean;
  legacyTimelineIsAvailable: boolean;
  newTimelineIsAvailable: boolean;
  timelinesList: TimelineDropdownItem[];
  selectedView: View;
  handleViewChange: (view: View) => void;
}) => {
  const statsContext = useContext(StatsContext);
  if (overflow) {
    return <div id="AssistantButton" role="button" tabIndex={0} aria-label="Open Assistant chat" onClick={event => {
      handleViewChange('chat');
      recordStat('OpenAssistantButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
    }} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleViewChange('chat');
        recordStat('OpenAssistantButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
      }
    }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer">
        <div className="w-[22px] h-[22px] flex items-center justify-center">
          <Image className="w-[22px] h-[22px]" alt="" src="/icons/assistant-small-white.svg" width={22} height={22} priority role="presentation" />
        </div>
        <span className={`whitespace-nowrap ${selectedView === 'chat' ? 'font-semibold' : ''}`}>Assistant</span>
      </div>;
  }
  return <div className={`dropdown-item ${overflow ? 'ml-[-8px]' : ''}`} data-sentry-component="Chat" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <button id="AssistantButton" aria-label="Open Assistant chat" className={`icon-text-btn z-[2] sm:bg-white/10 sm:rounded-l-2xl sm:rounded-r-sm sm:hover:rounded-r-sm ${selectedView === 'chat' ? 'sm:rounded-l-2xl' : 'text-white/70'}`} data-state={selectedView === 'chat' ? 'selected' : 'default'} onClick={event => {
      handleViewChange('chat');
      recordStat('OpenAssistantButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
    }}>
        <Image className="w-[22px] h-[22px]" alt="" src="/icons/assistant.svg" width={5} height={12} priority role="presentation" data-sentry-element="Image" data-sentry-source-file="navigation-bar-horizontal.tsx" />
        <div className={`whitespace-nowrap ${overflow ? 'text-[18px]' : 'hidden md2:inline'}`}>Assistant</div>
      </button>
    </div>;
};
const Details = ({
  featureFlags,
  handleViewChange,
  setLastSelectedView,
  setWasClicked,
  overflow
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  handleViewChange: (view: View) => void;
  setLastSelectedView: (view: View) => void;
  setWasClicked: (clicked: boolean) => void;
  overflow: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  return <div id="DetailsButton" role="button" tabIndex={0} aria-label="Switch to Details view" onClick={event => {
    handleViewChange('table');
    setLastSelectedView('table');
    setWasClicked(false);
    recordStat('OpenDetailsButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewChange('table');
      setLastSelectedView('table');
      setWasClicked(false);
      recordStat('OpenDetailsButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
    }
  }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer" data-sentry-component="Details" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px] flex items-center justify-center"><ListDashesIcon size={22} data-sentry-element="ListDashesIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      <span className="whitespace-nowrap">Details</span>
    </div>;
};
const Presentation = ({
  featureFlags,
  handleViewChange,
  setLastSelectedView,
  setWasClicked,
  overflow
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  handleViewChange: (view: View) => void;
  setLastSelectedView: (view: View) => void;
  setWasClicked: (clicked: boolean) => void;
  overflow: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  return <div id="PresentationButton" role="button" tabIndex={0} aria-label="Switch to Presentation view" onClick={event => {
    handleViewChange('presentation');
    setLastSelectedView('presentation');
    setWasClicked(false);
    recordStat('OpenPresentationButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewChange('presentation');
      setLastSelectedView('presentation');
      setWasClicked(false);
      recordStat('OpenPresentationButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
    }
  }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer" data-sentry-component="Presentation" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px] flex items-center justify-center"><LayoutIcon size={22} data-sentry-element="LayoutIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      <span className="whitespace-nowrap">Presentation</span>
    </div>;
};
const Courtroom = ({
  featureFlags,
  handleViewChange,
  setLastSelectedView,
  setWasClicked,
  overflow
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  handleViewChange: (view: View) => void;
  setLastSelectedView: (view: View) => void;
  setWasClicked: (clicked: boolean) => void;
  overflow: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  return <div id="CourtroomButton" role="button" tabIndex={0} aria-label="Switch to Courtroom view" onClick={event => {
    handleViewChange('courtroom');
    setLastSelectedView('courtroom');
    setWasClicked(false);
    recordStat('OpenCourtroomButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewChange('courtroom');
      setLastSelectedView('courtroom');
      setWasClicked(false);
      recordStat('OpenCourtroomButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
    }
  }} className={`dropdown-item flex flex-row items-center gap-2 cursor-pointer ${overflow ? 'ml-1' : ''}`} data-sentry-component="Courtroom" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px] flex items-center justify-center"><LayoutIcon size={22} data-sentry-element="LayoutIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      <span className="whitespace-nowrap">Courtroom</span>
    </div>;
};
const AssistantMenuItem = ({
  featureFlags,
  overflow,
  selectedView,
  handleViewChange,
  onCloseMenu
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  overflow: boolean;
  selectedView: View;
  handleViewChange: (view: View) => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  return <div id="AssistantMenuItem" role="button" tabIndex={0} aria-label="Open Assistant chat" onClick={event => {
    handleViewChange('chat');
    if (onCloseMenu) {
      onCloseMenu();
    }
    recordStat('OpenAssistantButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
  }} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleViewChange('chat');
      if (onCloseMenu) {
        onCloseMenu();
      }
      recordStat('OpenAssistantButtonClicked', e as any, statsContext, !!featureFlags?.useIntercom);
    }
  }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer" data-sentry-component="AssistantMenuItem" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px] flex items-center justify-center">
        <Image src={selectedView === 'chat' ? "/icons/assistant-small-white.svg" : "/icons/assistant-small-light-white.svg"} alt="Assistant" width={22} height={22} priority className="w-[22px] h-[22px]" data-sentry-element="Image" data-sentry-source-file="navigation-bar-horizontal.tsx" />
      </div>
      <span className={`whitespace-nowrap ${selectedView === 'chat' ? 'font-semibold' : ''}`}>Assistant</span>
    </div>;
};
const WorkProductsMenuItem = ({
  featureFlags,
  overflow,
  selectedView,
  handleViewChange,
  onWorkProductsClick,
  onCloseMenu
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  overflow: boolean;
  selectedView: View;
  handleViewChange: (view: View) => void;
  onWorkProductsClick?: () => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (onWorkProductsClick) {
      onWorkProductsClick();
    } else {
      handleViewChange('work-product-index');
    }
    if (onCloseMenu) {
      onCloseMenu();
    }
    recordStat('OpenWorkProductsButtonClicked', event as any, statsContext, !!featureFlags?.useIntercom);
  };
  return <div id="WorkProductsMenuItem" role="button" tabIndex={0} aria-label="Open Work Products" onClick={handleClick} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  }} className="dropdown-item flex flex-row items-center gap-2 cursor-pointer" data-sentry-component="WorkProductsMenuItem" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div className="w-[22px] h-[22px] flex items-center justify-center"><FilesIcon size={22} weight={isWorkProductView(selectedView) ? 'bold' : 'regular'} data-sentry-element="FilesIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" /></div>
      <span className={`whitespace-nowrap ${isWorkProductView(selectedView) ? 'font-semibold' : ''}`}>Work Products</span>
    </div>;
};
const MultipleTimelinesDropdown = ({
  featureFlags,
  timelinesList,
  selectedSecondOption,
  handleSecondOptionChange,
  overflow
}: {
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  } | undefined;
  timelinesList: TimelineDropdownItem[];
  selectedSecondOption: string | null;
  handleSecondOptionChange: (newValue: SingleValue<{
    value: string;
    label: string;
  }>) => void;
  overflow: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  const [isOpen, setIsOpen] = useState(false);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  // Focus trap: Get all focusable elements within the popover
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!popoverContentRef.current) return [];
    const focusableSelectors = ['a[href]', 'button:not([disabled])', 'textarea:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])'].join(', ');
    return Array.from(popoverContentRef.current.querySelectorAll<HTMLElement>(focusableSelectors)).filter(el => {
      // Filter out elements that are not visible
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
  }, []);

  // Focus trap: Handle Tab key to cycle focus within popover
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !popoverContentRef.current || !isOpen) return;
    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    // If Shift+Tab on first element, move to last
    if (e.shiftKey && activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
      return;
    }

    // If Tab on last element, move to first
    if (!e.shiftKey && activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
      return;
    }
  }, [getFocusableElements, isOpen]);

  // Focus trap: Set up focus management when popover opens/closes
  useEffect(() => {
    if (isOpen) {
      // Save the previously focused element
      previousActiveElementRef.current = document.activeElement as HTMLElement;

      // Focus the first focusable element in the popover after a short delay
      // to ensure the popover is fully rendered
      setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else if (popoverContentRef.current) {
          // If no focusable elements, focus the popover container
          popoverContentRef.current.focus();
        }
      }, 0);

      // Add keydown listener for Tab key handling
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      // Restore focus to the previously focused element when popover closes
      if (previousActiveElementRef.current) {
        previousActiveElementRef.current.focus();
        previousActiveElementRef.current = null;
      }
    }
  }, [isOpen, getFocusableElements, handleKeyDown]);

  // Don't render this component when workProductNav feature flag is enabled
  if (featureFlags && featureFlags.workProductNav) {
    return null;
  }
  if (overflow) return <div onClick={e => {
    e.stopPropagation();
    e.preventDefault();
  }}>
      <Dropdown classNames={{
      base: '',
      content: 'z-[11] min-w-[100px] p-2 bg-white text-black rounded-[0.25rem] m-2 w-[200px] max-h-[200px] overflow-y-auto custom-scrollbar-2'
    }}>
        <DropdownTrigger>
          <button aria-label="Select a timeline" className="mt-1 mr-1.5 sm:mr-2">
            <div className="flex flex-row items-center pl-2 text-white text-[18px]">Select a timeline</div>
          </button>
        </DropdownTrigger>
        <DropdownMenu classNames={{
        base: 'p-0 top-[120px]',
        list: 'p-0 gap-0 w-full'
      }} aria-label="Timelines">
          <DropdownSection title="Timelines" classNames={{
          heading: 'text-gray-800 text-[16px] font-semibold',
          base: 'p-0 w-full'
        }} itemClasses={{
          base: 'py-1 w-full',
          wrapper: 'w-full',
          title: 'w-full',
          description: 'w-full',
          shortcut: 'w-full'
        }}>
            {timelinesList.map(item => {
            const createdBy = item.createdBy ? item.createdBy.split('@')[0] : '';
            const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) : '';
            const title = item.title ? item.title : '';
            return <DropdownItem id={`MultipleTimelinesDropdownItem`} key={item.id} classNames={{
              base: 'py-1 w-full',
              wrapper: 'w-full',
              title: 'w-full'
            }} onPress={e => {
              handleSecondOptionChange({
                value: item.id,
                label: item.id
              });
              recordStat('OpenMultipleTimelinesDropdownItemClicked', e as unknown as React.MouseEvent, statsContext, !!featureFlags?.useIntercom);
            }}>
                  <div className="flex flex-row border-1 border-solid border-black/30 bg-black/10 rounded-md items-center p-1 text-black text-[16px] text-wrap w-full">
                    {`${createdBy} | ${item.id} | ${createdAt} | ${title}`}
                  </div>
                </DropdownItem>;
          })}
          </DropdownSection>
        </DropdownMenu>
      </Dropdown>
    </div>;
  return <Popover isOpen={isOpen} onOpenChange={setIsOpen} classNames={{
    base: '',
    trigger: '',
    content: 'box-border flex items-center justify-between gap-3 w-[224px] max-h-[300px] overflow-y-auto custom-scrollbar-2 mt-[-7px] bg-white text-black rounded-2xl rounded-t-none border-1 border-solid border-white/10 hover:border-white/70 focus:border-white/50 focus:outline-none font-inter text-left py-2.5 ml-2'
  }} data-sentry-element="Popover" data-sentry-component="MultipleTimelinesDropdown" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="navigation-bar-horizontal.tsx">
        <button type="button" id="MultipleTimelinesDropdown" aria-label="Select a timeline" aria-haspopup="dialog" onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        recordStat('OpenMultipleTimelinesDropdownButtonClicked', e, statsContext, !!featureFlags?.useIntercom);
      }} className="box-border w-[266px] h-[38px] px-4 py-2 ml-2 text-white text-[14px] bg-white/10 rounded-3xl border-1 border-solid border-white/10 hover:border-white/70 focus:border-white/50 focus:outline-none font-inter text-left flex items-center justify-between cursor-pointer">
          {selectedSecondOption ? selectedSecondOption === 'Switch To Doc ID input' ? 'Switch To Doc ID input' : selectedSecondOption === 'Create New Timeline' ? 'Create New Timeline' : (() => {
          const timeline = timelinesList.find(item => item.id === selectedSecondOption);
          if (timeline) {
            const createdBy = timeline.createdBy ? timeline.createdBy.split('@')[0] : '';
            const createdAt = timeline.createdAt ? new Date(timeline.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) : '';
            const title = timeline.title || '';
            return <span className="max-w-[210px] truncate">{`Timeline: ${title}`}</span>;
          }
          return '';
        })() : "Select a timeline"}
          <CaretDownIcon size={16} className="w-4 h-4" data-sentry-element="CaretDownIcon" data-sentry-source-file="navigation-bar-horizontal.tsx" />
        </button>
      </PopoverTrigger>
      <PopoverContent data-sentry-element="PopoverContent" data-sentry-source-file="navigation-bar-horizontal.tsx">
        <div ref={popoverContentRef} tabIndex={-1} role="dialog" aria-label="Select a timeline">
      {timelinesList.length > 0 || !!featureFlags?.legacyTimelines ? <>
          <div className="box-border flex flex-row items-center gap-3 justify-between px-2">
            {featureFlags?.legacyTimelines && <button type="button" aria-label="Switch to Document ID input" className={`bg-black/10 rounded-md px-2 py-1 flex flex-col items-center gap-1 cursor-pointer w-[70px] ${selectedSecondOption === 'Switch To Doc ID input' ? 'bg-[#DEEBFF] text-blue-900' : 'hover:bg-[#DEEBFF] hover:text-blue-900'}`} onClick={() => {
              handleSecondOptionChange({
                value: 'Switch To Doc ID input',
                label: 'Switch To Doc ID input'
              });
              setIsOpen(false);
            }}>
                <TextboxIcon size={22} />Doc ID
              </button>}
            <button type="button" aria-label="Create new timeline" className={`bg-black/10 rounded-md px-2 py-1 flex flex-col items-center gap-1 cursor-pointer w-[90px] ${selectedSecondOption === 'Create New Timeline' ? 'bg-[#DEEBFF] text-blue-900' : 'hover:bg-[#DEEBFF] hover:text-blue-900'}`} onClick={() => {
              handleSecondOptionChange({
                value: 'Create New Timeline',
                label: 'Create New Timeline'
              });
              setIsOpen(false);
            }}>
              <PlusIcon size={22} />New Timeline
            </button>
          </div>
          {timelinesList.map((item, index) => {
            const createdBy = item.createdBy ? item.createdBy.split('@')[0] : '';
            const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }) : '';
            const title = item.title || '';
            return <button type="button" aria-label={`Select timeline: ${title && title.trim() !== '' ? title : 'No Title Slide'}`} className={`mt-1 mx-1 border-1 border-solid border-[#f4f4f4] text-black rounded-lg px-2 py-1 flex flex-row items-center gap-2 cursor-pointer ${item.id === selectedSecondOption ? 'bg-[#DEEBFF] text-blue-900' : 'hover:bg-[#DEEBFF] hover:text-blue-900 bg-black/5'} text-[12px]`} key={index} onClick={() => {
              handleSecondOptionChange({
                value: item.id,
                label: `${createdBy} | ${item.id} | ${createdAt} | ${title}`
              });
              setIsOpen(false);
            }}>
                <div className="flex flex-col gap-2 leading-[13px]">
                  <span className="font-semibold text-wrap break-all">{title && title.trim() !== '' ? title : 'No Title Slide'}</span>
                  <div className="flex flex-row items-center justify-between gap-1">
                    <span className={`flex flex-row items-center gap-1 rounded-sm px-1 py-0.5 ${item.id === selectedSecondOption ? 'bg-white/50' : 'bg-black/5'}`}><UserCircleIcon size={12} weight="light" /> {createdBy}</span>
                    <span className={`flex flex-row items-center gap-1 rounded-sm px-1 py-0.5 ${item.id === selectedSecondOption ? 'bg-white/50' : 'bg-black/5'}`}><CalendarDotsIcon size={12} weight="light" /> {createdAt}</span>
                  </div>
                  <span className="italic text-[10px] text-black/50">{item.id}</span>
                </div>
              </button>;
          })}
        </> : <span key="no-timelines">No timelines available</span>}
        </div>
      </PopoverContent>
    </Popover>;
};
export type NavigationBarHorizontalType = {
  className?: string;
  selectedOption: string | null;
  onOptionChange: (option: string | undefined) => void;
  selectedSecondOption: string | null;
  onSecondOptionChange: (option: string | undefined) => void;
  userId: string;
  onViewChange: (view: View) => void;
  selectedView: View;
  onQueryKeyChange: (queryKey: string) => void;
  newTimelineIsAvailable: boolean;
  legacyTimelineIsAvailable: boolean;
  multipleTimelinesAvailable: TimelineDropdownItem[];
  loadingTimeline: boolean;
  clientWidth: number | undefined;
  onFeedbackClick: () => void;
  onCreateTimeline: () => void;
  creatingTimeline: boolean;
  timelineCreated: boolean;
  timelineServiceAppearsOperational: boolean;
  onOpenSettings: () => void;
  currentTimelineId: string | undefined;
  docIdMode: boolean;
  handleLogout: () => void;
  setTenantName: (tenantName: string) => void;
  setRemoteStorageLink: (remoteStorage: RemoteStorage | null) => void;
  setOrgName: (orgName: string) => void;
  isUserAdmin?: boolean;
  isUserServiceAvailable?: boolean;
  onOpenAdmin?: () => void;
  onWorkProductsClick?: () => void;
  setBanners: (banners: string[]) => void;
};
const NavigationBarHorizontal: NextPage<NavigationBarHorizontalType> = ({
  className = "",
  selectedOption,
  onOptionChange,
  selectedSecondOption,
  onSecondOptionChange,
  userId,
  onViewChange,
  selectedView,
  onQueryKeyChange,
  newTimelineIsAvailable,
  legacyTimelineIsAvailable,
  multipleTimelinesAvailable,
  loadingTimeline,
  clientWidth,
  onFeedbackClick,
  onCreateTimeline,
  creatingTimeline,
  timelineCreated,
  timelineServiceAppearsOperational,
  onOpenSettings,
  currentTimelineId,
  docIdMode,
  handleLogout,
  setTenantName,
  setRemoteStorageLink,
  setOrgName,
  isUserAdmin,
  isUserServiceAvailable,
  onOpenAdmin,
  onWorkProductsClick,
  setBanners
}) => {
  const [options, setOptions] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queryKey, setQueryKey] = useState('');
  const [submittedQueryKey, setSubmittedQueryKey] = useState('');
  const [showHotkeysModal, setShowHotkeysModal] = useState(false);
  const [lastSelectedView, setLastSelectedView] = useState<string | null>(null);
  const [wasClicked, setWasClicked] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [highlightStyle, setHighlightStyle] = useState<{
    left: number;
    width: number;
  }>({
    left: 10,
    width: 100
  });
  const [casePlaceHolder, setCasePlaceHolder] = useState("Select or type case name");
  const [isOpen, setIsOpen] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [timelinesList, setTimelinesList] = useState<TimelineDropdownItem[]>([]);
  const [tenantNameLocal, setTenantNameLocal] = useState<string | undefined>(undefined);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const statsContext = useContext(StatsContext);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  const mediaSourceShare = useMediaSourceShare();
  const toggleMediaSourcesDrawer = !featureFlags?.enableMediaAndSourceShare ? mediaSourceShare?.toggleDrawer : undefined;
  const navRef = useRef<HTMLDivElement>(null);
  const navLeftRef = useRef<HTMLDivElement>(null);
  const caseDropdownLeftRef = useRef<HTMLDivElement>(null);
  const assistantButtonRef = useRef<HTMLDivElement>(null);
  const timelineButtonRef = useRef<HTMLDivElement>(null);
  const documentIdInputRef = useRef<HTMLInputElement>(null);
  const caseDropdownRightRef = useRef<HTMLDivElement>(null);
  const navRightRef = useRef<HTMLDivElement>(null);
  const [overflowMenuItems, setOverflowMenuItems] = useState<any[]>([]);
  const [overflowLevel, setOverflowLevel] = useState(0);
  useEffect(() => {
    if (tenantNameLocal) {
      setTenantName(tenantNameLocal);
    }
  }, [tenantNameLocal]);
  useEffect(() => {
    if (multipleTimelinesAvailable) {
      setTimelinesList(multipleTimelinesAvailable.sort((a, b) => {
        if (a.createdBy === userId) {
          return -1;
        } else if (b.createdBy === userId) {
          return 1;
        }
        const aDate = new Date(a.createdAt);
        const bDate = new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      }));
    }
  }, [multipleTimelinesAvailable, userId]);
  const fetchOptions = useCallback(async () => {
    setIsLoadingCases(true);
    try {
      const response = await FetchWithVersionCheck('/api/protected/cases/*', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          primary_only: true
        })
      });
      const data = await response.json();
      let tenantLocal: string | undefined = undefined;
      if (data.tenantName && data.tenantName.trim() !== '') {
        tenantLocal = data.tenantName;
      } else if (data.tenant && data.tenant.trim() !== '') {
        tenantLocal = data.tenant;
      }
      if (tenantLocal && tenantLocal.trim() !== '') {
        document.title = `TimePilot ${tenantLocal.toUpperCase()}`;
        setTenantNameLocal(tenantLocal);
        if (data?.remoteStorage?.url && data.remoteStorage.url !== '') {
          setRemoteStorageLink({
            type: data.remoteStorage.type || 'SharePoint',
            url: data.remoteStorage.url
          });
        } else {
          setRemoteStorageLink(null);
        }
      }
      if (data.orgName && data.orgName.trim() !== '') {
        setOrgName(data.orgName);
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch cases: ${response.statusText}`);
      }
      if (!data.options) {
        throw new Error('No cases available');
      }
      setBanners(data.banners || []);
      // Sort the options alphabetically
      let sortedOptions = data.options.sort((a: string, b: string) => a.localeCompare(b));
      if (!!featureFlags?.restrictedCases && Object.keys(featureFlags?.restrictedCases).length > 0) {
        const restrictedCases = featureFlags?.restrictedCases as {
          [key: string]: string[];
        };
        const unrestrictedUsers = restrictedCases["unrestrictedUsers"] as string[] || [];
        const userIdLowerCase = userId.toLowerCase();
        sortedOptions = sortedOptions.filter((option: string) => {
          const caseId = option.toLowerCase();
          // Collect all matching restriction patterns first
          const matchingPatterns: Array<{
            users: string[];
            deniesUnrestricted: boolean;
          }> = [];
          for (const entry of Object.entries(restrictedCases).filter((entry: [string, string[]]) => entry[0] !== 'unrestrictedUsers')) {
            const restrictedCase = entry[0].toLowerCase();
            if (caseId === restrictedCase || caseId.startsWith(`${restrictedCase}/`)) {
              const lowerCaseUsers = entry[1]?.map((user: string) => user.toLowerCase()) || [];
              matchingPatterns.push({
                users: lowerCaseUsers,
                deniesUnrestricted: lowerCaseUsers.includes('no_unrestricted_access')
              });
            }
          }
          if (matchingPatterns.length === 0) {
            return true;
          }
          const isExplicitlyAllowed = matchingPatterns.some(pattern => pattern.users.includes(userIdLowerCase));
          if (isExplicitlyAllowed) {
            return true;
          }
          const isUnrestrictedUser = unrestrictedUsers.includes(userIdLowerCase);
          const anyDeniesUnrestricted = matchingPatterns.some(pattern => pattern.deniesUnrestricted);
          if (isUnrestrictedUser && !anyDeniesUnrestricted) {
            return true;
          }
          return false;
        });
      }
      setOptions(sortedOptions);
      const caseIdFromUrl = sessionStorage.getItem('caseIdFromUrl');
      if (caseIdFromUrl && caseIdFromUrl.trim() !== '') {
        if (sortedOptions.includes(caseIdFromUrl)) {
          onOptionChange(caseIdFromUrl);
          sessionStorage.removeItem('caseIdFromUrl');
        } else if (!!tenantLocal && sortedOptions.includes(`${tenantLocal}/${caseIdFromUrl}`)) {
          onOptionChange(`${tenantLocal}/${caseIdFromUrl}`);
          sessionStorage.removeItem('caseIdFromUrl');
        } else if (!!tenantLocal && sortedOptions.includes(encodeURIComponent(`${tenantLocal}/` + caseIdFromUrl))) {
          onOptionChange(encodeURIComponent(`${tenantLocal}/` + caseIdFromUrl));
          sessionStorage.removeItem('caseIdFromUrl');
        } else {
          sessionStorage.removeItem('caseIdFromUrl');
        }
      }
    } catch (error) {
      const errorMessage = `Error fetching cases for user ${userId}: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
      // alert(errorMessage);
    } finally {
      setIsLoadingCases(false);
    }
  }, [userId, featureFlags?.restrictedCases]);
  useEffect(() => {
    if (!!accessToken && !!idToken && !!featureFlags) {
      fetchOptions();
    }
  }, [fetchOptions, featureFlags?.restrictedCases, accessToken, idToken]);
  useEffect(() => {
    if (isLoadingCases) return;
    if (!options.length) return;
    if (selectedOption && selectedOption.trim() !== '' && selectedOption !== '*' && !options.includes(selectedOption) && !(!!tenantNameLocal && options.includes(`${tenantNameLocal}/${selectedOption}`)) && !(!!tenantNameLocal && options.includes(encodeURIComponent(`${tenantNameLocal}/` + selectedOption)))) {
      onOptionChange(undefined);
    }
  }, [featureFlags?.restrictedCases, options, selectedOption, tenantNameLocal, isLoadingCases, onOptionChange]);
  const handleOptionChange = useCallback((newValue: SingleValue<{
    value: string;
    label: string;
  }>) => {
    setQueryKey('');
    setSubmittedQueryKey('');
    sessionStorage.setItem('queryKey', '');
    if (documentIdInputRef?.current) {
      documentIdInputRef.current.value = '';
    }
    onOptionChange(newValue ? newValue.value : undefined);
  }, [onOptionChange, setQueryKey, setSubmittedQueryKey, documentIdInputRef]);
  const handleSecondOptionChange = useCallback((newValue: SingleValue<{
    value: string;
    label: string;
  }>) => {
    onSecondOptionChange(newValue ? newValue.value : undefined);
  }, [onSecondOptionChange]);
  const handleSyncData = useCallback(async () => {
    if (!selectedOption) {
      alert("Please select a case before syncing data.");
      return;
    }
    setIsSyncing(true);
    try {
      const result = await FetchWithVersionCheck('/api/protected/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId,
          selectedCase: selectedOption
        })
      });
      if (result.ok) {
        alert("Data sync initiated successfully!");
      } else {
        const errorMessage = `Failed to sync data: ${result.statusText}`;
        CaptureError(errorMessage, new Error(errorMessage), !!featureFlags?.useIntercom);
        alert(errorMessage);
      }
    } catch (error) {
      const errorMessage = `Error syncing data for user ${userId}: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
      alert("An error occurred while syncing data. Please try again.");
    } finally {
      setIsSyncing(false);
    }
  }, [userId, selectedOption, featureFlags?.useIntercom]);
  const handleQueryKeyChange = useCallback((newQueryKey: string) => {
    setQueryKey(newQueryKey);
  }, [setQueryKey]);
  const handleQueryKeySubmit = useCallback((newQueryKey: string) => {
    if (!newQueryKey || newQueryKey.trim() === '') {
      onOptionChange(selectedOption || undefined);
      return;
    }
    if (typeof onQueryKeyChange === 'function') {
      onQueryKeyChange(newQueryKey);
    }
    setLastSelectedView('table');
    setSubmittedQueryKey(newQueryKey);
  }, [onQueryKeyChange, onOptionChange, selectedOption, setLastSelectedView, setSubmittedQueryKey]);
  const handleViewChange = useCallback((view: View) => {
    onViewChange(view);
  }, [onViewChange]);
  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const MenuItems: Record<string, any> = useMemo(() => {
    return {
      Sync: <Sync featureFlags={featureFlags} handleSyncData={handleSyncData} isSyncing={isSyncing} overflow={true} />,
      Logout: <Logout featureFlags={featureFlags} handleLogout={handleLogout} overflow={true} />,
      Hotkeys: <Hotkeys setShowHotkeysModal={setShowHotkeysModal} overflow={true} featureFlags={featureFlags} />,
      DocumentIdInput: <DocumentIdInput featureFlags={featureFlags} documentIdInputRef={documentIdInputRef} closeDropdown={closeDropdown} queryKey={queryKey} handleQueryKeyChange={handleQueryKeyChange} handleQueryKeySubmit={handleQueryKeySubmit} />,
      Details: <Details featureFlags={featureFlags} overflow={true} handleViewChange={handleViewChange} setLastSelectedView={setLastSelectedView} setWasClicked={setWasClicked} />,
      Presentation: <Presentation featureFlags={featureFlags} overflow={true} handleViewChange={handleViewChange} setLastSelectedView={setLastSelectedView} setWasClicked={setWasClicked} />,
      Courtroom: featureFlags?.courtroomMode ? <Courtroom featureFlags={featureFlags} overflow={true} handleViewChange={handleViewChange} setLastSelectedView={setLastSelectedView} setWasClicked={setWasClicked} /> : null,
      Chat: !featureFlags?.workProductNav ? <Chat featureFlags={featureFlags} overflow={true} loadingTimeline={loadingTimeline} legacyTimelineIsAvailable={legacyTimelineIsAvailable} newTimelineIsAvailable={newTimelineIsAvailable} timelinesList={timelinesList} selectedView={selectedView} handleViewChange={handleViewChange} /> : null,
      Feedback: <FeedbackButton onFeedbackClick={onFeedbackClick} />,
      MultipleTimelines: <MultipleTimelinesDropdown featureFlags={featureFlags} overflow={true} timelinesList={timelinesList} selectedSecondOption={selectedSecondOption} handleSecondOptionChange={handleSecondOptionChange} />,
      Assistant: featureFlags?.workProductNav ? <AssistantMenuItem featureFlags={featureFlags} overflow={true} selectedView={selectedView} handleViewChange={handleViewChange} onCloseMenu={() => setIsOpen(false)} /> : null,
      WorkProducts: featureFlags?.workProductNav ? <WorkProductsMenuItem featureFlags={featureFlags} overflow={true} selectedView={selectedView} handleViewChange={handleViewChange} onWorkProductsClick={onWorkProductsClick} onCloseMenu={() => setIsOpen(false)} /> : null
    };
  }, [featureFlags, selectedSecondOption, selectedView, onFeedbackClick, timelinesList, handleQueryKeySubmit, handleSecondOptionChange, handleSyncData, handleViewChange, isSyncing, legacyTimelineIsAvailable, loadingTimeline, newTimelineIsAvailable, queryKey, closeDropdown, handleQueryKeyChange, onWorkProductsClick, toggleMediaSourcesDrawer]);
  const overflowLevels: {
    level: number;
    breakpoint: number;
    items: any[];
  }[] = useMemo(() => [{
    level: 0.5,
    breakpoint: 940,
    items: [MenuItems.MultipleTimelines]
  }, {
    level: 1,
    breakpoint: 795,
    items: [MenuItems.MediaSources, MenuItems.Sync, MenuItems.Hotkeys, MenuItems.Logout].filter(item => item !== null)
  }, {
    level: 2,
    breakpoint: 790,
    items: featureFlags && featureFlags.workProductNav ? [] : [MenuItems.DocumentIdInput]
  }, {
    level: 2.5,
    breakpoint: 640,
    items: featureFlags && featureFlags.workProductNav ? [MenuItems.Assistant, MenuItems.WorkProducts].filter(item => item !== null) : []
  }, {
    level: 3,
    breakpoint: 635,
    items: [MenuItems.Chat, MenuItems.Presentation, MenuItems.Courtroom, MenuItems.Details].filter(item => item !== null)
  }, {
    level: 4,
    breakpoint: 520,
    items: [MenuItems.Feedback]
  }, {
    level: 5,
    breakpoint: TINY_VIEWPORT_WIDTH,
    items: []
  }], [MenuItems, featureFlags]);
  useEffect(() => {
    if (clientWidth) {
      let newOverflowMenuItems: any[] = [];
      let newOverflowLevel = 0;
      for (const level of overflowLevels) {
        if (clientWidth < level.breakpoint) {
          if (level.level < 4) {
            newOverflowMenuItems.unshift(...level.items);
          } else {
            newOverflowMenuItems.push(...level.items);
          }
          newOverflowLevel = level.level;
        }
      }
      if (timelinesList.length < 1) {
        newOverflowMenuItems = newOverflowMenuItems.filter(item => item !== MenuItems.MultipleTimelines);
      }
      if (featureFlags?.workProductNav) {
        newOverflowMenuItems = newOverflowMenuItems.filter(item => item !== MenuItems.Chat && item !== MenuItems.Details && item !== MenuItems.Courtroom && item !== MenuItems.DocumentIdInput);
        // Only show Assistant and WorkProducts in overflow menu when selectedOption is truthy
        if (!selectedOption) {
          newOverflowMenuItems = newOverflowMenuItems.filter(item => item !== MenuItems.Assistant && item !== MenuItems.WorkProducts);
        }
      }
      if (featureFlags && featureFlags.workProductNav) {
        newOverflowMenuItems = newOverflowMenuItems.filter(item => item !== MenuItems.MultipleTimelines);
      }
      if (!newTimelineIsAvailable && !legacyTimelineIsAvailable && timelinesList.length < 1) {
        newOverflowMenuItems = newOverflowMenuItems.filter(item => item !== MenuItems.Details && item !== MenuItems.Presentation && item !== MenuItems.Courtroom && item !== MenuItems.MultipleTimelines);
      }
      // Always add Logout at the end, removing it first if it exists elsewhere
      const logoutItem = newOverflowMenuItems.find(item => item === MenuItems.Logout);
      newOverflowMenuItems = newOverflowMenuItems.filter(item => item !== MenuItems.Logout);
      if (MenuItems.Logout) {
        newOverflowMenuItems.push(MenuItems.Logout);
      }
      setOverflowMenuItems(newOverflowMenuItems);
      setOverflowLevel(newOverflowLevel);
      if (newOverflowLevel < 5) {
        setCasePlaceHolder("Select or type case name");
      } else {
        setCasePlaceHolder("Select a case");
      }
    }
  }, [featureFlags, MenuItems, overflowLevels, clientWidth, newTimelineIsAvailable, legacyTimelineIsAvailable, timelinesList, selectedOption]);
  const showSecondDropdown = !!selectedOption && !docIdMode && !(featureFlags && featureFlags.workProductNav);
  const showDocumentIdInput = featureFlags && featureFlags?.legacyTimelines && docIdMode && !featureFlags?.workProductNav;
  const showUserAdmin = featureFlags && featureFlags['useUserService'];
  return <nav ref={navRef} id="main-navigation" role="navigation" aria-label="Main navigation" className={`[background:linear-gradient(92.29deg,_#2d8dfe,_#0164d6)]
        flex flex-row fixed w-screen z-[99999] top-0 justify-between items-center py-0 sm:py-1.5
        ${overflowLevel < 5 ? 'pl-[10px] pr-[15px] gap-5 text-[24px]' : 'pl-[0px] pr-[0px] gap-1 text-[20px]'} 
        box-border leading-[normal] tracking-[normal]
        text-left text-titlebar-button-fg font-inter
        ${className}`} data-sentry-component="NavigationBarHorizontal" data-sentry-source-file="navigation-bar-horizontal.tsx">
      <div ref={navLeftRef} className="flex flex-row gap-1 max-w-full items-center">
        <div className="flex flex-row pl-3.5 py-2 sm:py-0 sm:pl-2 sm:pr-3">
          <Tooltip content={<div className="p-2 rounded-md">
                <div>
                  {`TimePilot ${GetBuildId() ? `v${GetBuildId()}` : ''}`}
                </div>
                <div>
                  {`Tenant: ${tenantNameLocal || 'N/A'}`}
                </div>
                <div>
                  {`User: ${userId}`}
                </div>
                {featureFlags?.helioTraceAccess === "enabled" && <button type="button" className="mt-2 mr-2 flex w-[95%] flex-row items-center justify-center gap-2 rounded border border-white/30 bg-brand-blue px-2 py-1.5 text-sm text-white hover:bg-brand-blue-light" onClick={() => {
            window.location.href = '/immigration-reports';
          }}>
                    <span className="whitespace-nowrap">Go to</span>
                    <img src="/helio-logo-white.svg" alt="" className="h-5 w-auto object-contain" width={80} height={20} />
                  </button>}
              </div>} offset={-5} closeDelay={1000} showArrow={true} classNames={{
          content: 'bg-tooltip-bg text-white',
          base: 'before:bg-tooltip-bg before:ml-[-20%]'
        }} data-sentry-element="Tooltip" data-sentry-source-file="navigation-bar-horizontal.tsx">
            <div className="flex flex-row items-center w-[39px] sm:w-[140px] h-[40px] sm:h-[48.276px] overflow-hidden sm:py-[3px]">
              <img className="relative object-cover object-left sm:object-contain h-[55px] sm:w-full sm:h-full" alt="" src="/tp-white-r.png" width={140} height={48.276} role="presentation" />
            </div>
          </Tooltip>

        </div>
        <div ref={caseDropdownLeftRef} className={`flex flex-row gap-2 items-center ${overflowLevel < 4 ? 'min-w-[120px] sm:min-w-[266px]' : 'min-w-[120px] sm:min-w-[258px]'}`}>
          {isLoadingCases ? <div className="box-border flex flex-row items-center min-w-[120px] sm:min-w-[258px] h-[38px] ml-0 mr-[-8px] sm:ml-1 pl-[6px] text-[16px] text-white font-light font-inter bg-white/10 sm:rounded-3xl gap-2 resize-none placeholder-white focus:placeholder-transparent focus:outline-none border-white border-opacity-10 border-[1px] border-solid focus:border-white/50 hover:border-white/70 rounded-full">
              <CircleDashedIcon size={26} weight="bold" className="animate-spin-smooth2 text-white" />
              <span className="text-[14px] text-white font-light font-inter">Loading cases<span className="animate-pulse">...</span></span>
            </div> : <Walkthru walkthruName="pickCase" index={0} placement="bottom" width="350px">
              <div>
                <Select aria-label="Select case" styles={SelectStyles} className="react-select-container font-inter z-[9999] pr-2 w-full custom-scrollbar2" value={selectedOption ? {
              value: selectedOption,
              label: selectedOption === '*' ? 'All Cases' : (() => {
                const withoutTenant = tenantNameLocal && selectedOption.startsWith(`${tenantNameLocal}/`) ? selectedOption.replace(`${tenantNameLocal}/`, '') : selectedOption;
                return withoutTenant.split('/').pop() || withoutTenant;
              })()
            } : null} onChange={handleOptionChange as (newValue: unknown) => void} options={options.length > 0 ? [...['*', ...options].map(option => {
              const label = option === '*' ? 'All Cases' : option.split('/').pop() || option;
              return {
                value: option,
                label: label
              };
            }).filter(option => tenantNameLocal ? option.value.startsWith(`${tenantNameLocal}/`) : option.value !== '')] : [{
              value: '',
              label: 'No cases available',
              isDisabled: true
            }]} placeholder={casePlaceHolder} noOptionsMessage={() => "No cases available with that name"} />
              </div>
            </Walkthru>}
        </div>
        {featureFlags?.workProductNav && selectedOption && <div className="hidden sm:flex flex-row items-center gap-1 bg-white/10 border border-white/50 rounded-full p-1 ml-2 h-[30px]">

            <button aria-label="Open Assistant chat" onClick={e => {
          onViewChange('chat');
          recordStat('OpenAssistantChatButtonClicked', e, statsContext, !!featureFlags?.useIntercom);
        }} className={`flex flex-row items-center gap-2 ${overflowLevel > 0 ? 'px-2' : 'px-2'} transition-colors cursor-pointer h-[30px] ${selectedView === 'chat' ? 'border border-solid border-white rounded-full text-white' : 'text-white hover:bg-white/10 focus:bg-white/10 rounded-full'}`}>
              <Image src={selectedView === 'chat' ? "/icons/assistant-small-white.svg" : "/icons/assistant-small-light-white.svg"} alt="Assistant" width={18} height={18} priority />
              {overflowLevel === 0 && <span className="text-sm">Assistant</span>}
            </button>
            <button aria-label="Open Work Products" onClick={e => {
          if (onWorkProductsClick) {
            onWorkProductsClick();
          } else {
            onViewChange('work-product-index');
          }
          recordStat('OpenWorkProductsButtonClicked', e, statsContext, !!featureFlags?.useIntercom);
        }} className={`flex flex-row items-center gap-2 ${overflowLevel > 0 ? 'px-2' : 'px-2'} transition-colors cursor-pointer h-[30px] ${isWorkProductView(selectedView) ? 'border border-solid border-white rounded-full text-white' : 'text-white hover:bg-white/10 focus:bg-white/10 rounded-full'}`}>
              <FilesIcon size={18} weight={isWorkProductView(selectedView) ? 'bold' : 'regular'} />
              {overflowLevel === 0 && <span className="text-sm">Work Products</span>}
            </button>
          </div>}
        {overflowLevel < 3 && !featureFlags?.workProductNav && <div ref={assistantButtonRef} className="flex flex-row py-[0px] pl-[2px] pr-[4px] items-center relative bg-white/10 rounded-full divide-x divide-white/5 gap-[2px]">
            <Chat featureFlags={featureFlags} overflow={false} loadingTimeline={loadingTimeline} legacyTimelineIsAvailable={legacyTimelineIsAvailable} newTimelineIsAvailable={newTimelineIsAvailable} timelinesList={timelinesList} selectedView={selectedView} handleViewChange={handleViewChange} />
            {timelineServiceAppearsOperational && featureFlags && !loadingTimeline && !newTimelineIsAvailable && !legacyTimelineIsAvailable && timelinesList.length < 1 ? <div className="flex flex-row flex-wrap gap-2">
                {!creatingTimeline && selectedOption && selectedOption.trim() !== '' && <>
                    {!docIdMode ? <button id="CreateTimelineButton" aria-label="Create Timeline" role="button" className={`flex flex-row icon-text-btn bg-white/10 items-center gap-2 rounded-r-2xl rounded-l-sm hover:rounded-l-sm ${selectedView === 'courtroom' ? '' : 'text-white/70'}`} onClick={event => {
              onCreateTimeline();
              recordStat('CreateTimelineButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
            }} data-state={selectedView === 'courtroom' ? 'selected' : ''} disabled={creatingTimeline || timelineCreated}>
                        <Image className="w-[22px] h-[22px] pt-[1px]" alt="" src="/icons/timeline.svg" width={5} height={10} priority role="presentation" />
                        Create Timeline
                      </button> : <button id="CreateTimelineButton" aria-label="Switch to Timeline Mode" role="button" className={`flex flex-row icon-text-btn bg-white/10 items-center gap-2 rounded-r-2xl rounded-l-sm hover:rounded-l-sm ${selectedView === 'courtroom' ? '' : 'text-white/70'}`} onClick={event => handleSecondOptionChange({
              value: 'Switch To Timeline Mode',
              label: 'Switch To Timeline Mode'
            })} data-state={selectedView === 'courtroom' ? 'selected' : ''} disabled={creatingTimeline || timelineCreated}>
                      <Image className="w-[22px] h-[22px] pt-[1px]" alt="" src="/icons/timeline.svg" width={5} height={10} priority role="presentation" />
                      Timeline
                    </button>}
                  </>}
                {creatingTimeline && <div className="flex flex-row icon-text-btn bg-white/10 items-center gap-2 rounded-r-2xl rounded-l-sm hover:rounded-l-sm">
                    <div className="w-[20px] h-[20px] flex items-center justify-center px-[1px]">
                      <CircleDashedIcon size={20} weight="bold" className="animate-spin-smooth2 rounded-full p-0 m-0" />
                    </div>
                    <span className="text-[14px] hidden md2:inline">Create Timeline</span>
                  </div>}
              </div> : !featureFlags?.workProductNav && <div ref={timelineButtonRef}>
                <Dropdown isOpen={wasClicked} onOpenChange={setWasClicked} placement="bottom-start" classNames={{
            base: '',
            content: 'dropdown-content min-w-[100px] mt-[-3px] -ml-2 w-full font-inter text-[14px] bg-[#59A2F7] rounded-[0.125rem]'
          }}>
                  <div id="timeline-controls" className={`flex flex-row items-center rounded-md hover:cursor-pointer outline-none`}>
                    <DropdownTrigger>
                        <button id="TimelineControlsButton" aria-label={loadingTimeline ? "Loading timeline" : lastSelectedView === 'presentation' ? "Timeline: Presentation view" : lastSelectedView === 'courtroom' ? "Timeline: Courtroom view" : lastSelectedView === 'table' ? "Timeline: Details view" : "Timeline controls"} disabled={loadingTimeline || !legacyTimelineIsAvailable && !newTimelineIsAvailable && timelinesList.length < 1} className={`flex flex-row icon-text-btn bg-white/10 items-center gap-2 rounded-r-2xl rounded-l-sm hover:rounded-l-sm outline-none ${selectedView === 'courtroom' ? '' : 'text-white/70'}`} data-state={selectedView !== 'chat' ? 'selected' : ''} onClick={e => {
                  recordStat('TimelineControlsButtonClicked', e, statsContext, !!featureFlags?.useIntercom);
                  if (!lastSelectedView) {
                    e.preventDefault();
                    handleViewChange('table');
                    setLastSelectedView('table');
                  } else if (selectedView === 'chat') {
                    e.preventDefault();
                    const viewToSet = lastSelectedView === 'courtroom' ? 'courtroom' : 'table';
                    handleViewChange(viewToSet);
                  } else {
                    setWasClicked(!wasClicked);
                  }
                }}>
                          {loadingTimeline ? <div className="flex flex-row items-center gap-1">
                              <div className="flex items-center justify-center w-[20px] h-[20px]">
                                <CircleDashedIcon size={20} weight="bold" className="animate-spin-smooth2 bg-brand-blue rounded-full p-0 m-0" />
                              </div>
                              <span className="hidden md2:inline">Timeline</span>
                            </div> : lastSelectedView === 'courtroom' ? <div className="flex items-center gap-2">
                              <div className="w-[22px] h-[22px]"><LayoutIcon weight="regular" size={22} /></div>
                              <span className="text-nowrap hidden md2:inline">Timeline: Courtroom</span>
                            </div> : lastSelectedView === 'table' ? <div className="flex items-center gap-2 text-nowrap outline-none focus:outline-none">
                              <div className="w-[22px] h-[22px]"><ListDashesIcon weight="regular" size={22} /></div>
                              <span className="text-nowrap hidden md2:inline">Timeline: Details</span>
                            </div> : <div className="flex items-center gap-2 outline-none focus:outline-none">
                              <Image className="w-[22px] h-[22px]" alt="" src="/icons/timeline.svg" width={5} height={12} priority role="presentation" />
                              <span className="hidden md2:inline">Timeline</span>
                            </div>}
                          {(newTimelineIsAvailable || legacyTimelineIsAvailable || timelinesList.length > 0) && <CaretDownIcon size={12} className="ml-1" />}
                        </button>
                    </DropdownTrigger>
                  </div>

                  <DropdownMenu classNames={{
              base: 'p-0',
              list: 'p-0 gap-0'
            }} aria-label="Timeline options">
                    <DropdownItem key="timeline-details" classNames={{
                base: 'hover:bg-black/10 rounded-md pr-2'
              }} onPress={() => {}}>
                      <Details featureFlags={featureFlags} handleViewChange={handleViewChange} setLastSelectedView={setLastSelectedView} setWasClicked={setWasClicked} overflow={true} />
                    </DropdownItem>
                    <DropdownItem key="timeline-presentation" classNames={{
                base: 'hover:bg-black/10 rounded-md pr-2'
              }} onPress={() => {}}>
                      <Presentation featureFlags={featureFlags} handleViewChange={handleViewChange} setLastSelectedView={setLastSelectedView} setWasClicked={setWasClicked} overflow={true} />
                    </DropdownItem>
                    {featureFlags?.courtroomMode ? <DropdownItem key="timeline-courtroom" classNames={{
                base: 'hover:bg-black/10 rounded-md pr-2'
              }} onPress={() => {}}>
                        <Courtroom featureFlags={featureFlags} handleViewChange={handleViewChange} setLastSelectedView={setLastSelectedView} setWasClicked={setWasClicked} overflow={true} />
                      </DropdownItem> : null}
                  </DropdownMenu>
                </Dropdown>
              </div>}
          </div>}
        {(featureFlags?.legacyTimelines || featureFlags?.powerUserOrg) && showDocumentIdInput && overflowLevel < 2 && <DocumentIdInput featureFlags={featureFlags} closeDropdown={closeDropdown} documentIdInputRef={documentIdInputRef} queryKey={queryKey} handleQueryKeyChange={handleQueryKeyChange} handleQueryKeySubmit={handleQueryKeySubmit} />}
        {showSecondDropdown && overflowLevel < 0.5 && <MultipleTimelinesDropdown featureFlags={featureFlags} overflow={overflowLevel > 0} timelinesList={timelinesList} selectedSecondOption={selectedSecondOption} handleSecondOptionChange={handleSecondOptionChange} />}
      </div>
      {!overflowLevel && <div ref={navRightRef} id="nav-buttons" className="flex items-center">
        <div className="flex flex-row gap-5">
          {featureFlags?.useUserService && isUserServiceAvailable && isUserAdmin && <button id="SettingsButton" aria-label="Open Settings" className="icon-text-btn-vertical" onClick={event => {
          onOpenSettings();
          recordStat('OpenSettingsButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
        }}>
              <div className="icon-nav-btn">
                <GearIcon size={28} weight="light" />
              </div>
              <span className={`whitespace-nowrap ${overflowLevel ? 'text-[18px]' : ''}`}>Settings</span>
            </button>}
          {featureFlags?.useIntercom ? <a href="https://intercom.help/tranquility-ai/en/" target="_blank" rel="noopener noreferrer" id="HelpButton" aria-label="Help" className="icon-text-btn-vertical no-underline text-inherit" onClick={event => {
          recordStat('HelpButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
        }}>
              <div className="icon-nav-btn">
                <QuestionIcon size={28} weight="light" />
              </div>
              <span className={`whitespace-nowrap ${overflowLevel ? 'text-[18px]' : ''}`}>Help</span>
            </a> : <Popover showArrow={true} placement="bottom-end" classNames={{
          content: 'flex flex-col justify-start items-start bg-white text-black w-[300px] p-4 pb-2 rounded-md'
        }}>
              <PopoverTrigger>
                <button type="button" id="HelpButton" aria-label="Help" aria-haspopup="dialog" className="icon-text-btn-vertical">
                  <div className="icon-nav-btn" onClick={event => {
                recordStat('HelpButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
              }}>
                    <QuestionIcon size={28} weight="light" />
                  </div>
                  <span className={`whitespace-nowrap ${overflowLevel ? 'text-[18px]' : ''}`}>Help</span>
                </button>
              </PopoverTrigger>
              <PopoverContent>
                <div className="w-full">
                  <h3 className="text-[22px] font-normal text-[#0F1D32] m-0 mb-3 p-0 border-black border-b-[3px] border-double pb-2 w-full">Help</h3>
                  <ul className="m-0 ml-2 p-0 text-[#0F1D32] text-[16px] list-none">
                    <li className="mb-3">
                      <a href="https://tranquility.ai/timepilot" target="_blank" className="flex flex-row items-center justify-left gap-2.5 mb-3 no-underline text-[#0F1D32] hover:text-brand-blue hover:cursor-pointer">
                        <FlagBannerFoldIcon size={24} weight="duotone" />
                        About TimePilot
                      </a>
                    </li>
                    <li className="mb-3">
                      <a href="https://tranquility.ai/get-started-guide" target="_blank" className="flex flex-row items-center justify-left gap-2.5 mb-3 no-underline text-[#0F1D32] hover:text-brand-blue hover:cursor-pointer">
                        <LightningIcon size={24} weight="duotone" />
                        Getting Started Guide
                      </a>
                    </li>
                    <li className="mb-3">
                      <a href="https://tranquility.ai/guides/assistant-guide" target="_blank" className="flex flex-row items-center justify-left gap-2.5 mb-3 no-underline text-[#0F1D32] hover:text-brand-blue hover:cursor-pointer">
                        <FileMagnifyingGlassIcon size={24} weight="duotone" />
                        Assistant Guide
                      </a>
                    </li>
                    <li className="mb-3">
                      <a href="https://tranquility.ai/guides/case-file-sync-guide" target="_blank" className="flex flex-row items-center justify-left gap-2.5 mb-3 no-underline text-[#0F1D32] hover:text-brand-blue hover:cursor-pointer">
                        <FileCloudIcon size={24} weight="duotone" />
                        Case File Sync Guide
                      </a>
                    </li>
                    <li className="mb-3">
                      <button id="HotkeysButton" onClick={event => {
                    setShowHotkeysModal(true);
                    setIsPopoverVisible(false);
                    recordStat('HotkeysButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
                  }} className="flex flex-row items-center justify-left hover:text-brand-blue gap-3 hover:cursor-pointer">
                        <KeyboardIcon size={22} weight="duotone" />
                        <span>Hotkeys</span>
                      </button>
                    </li>
                    <li className="mb-3">
                      <a href="https://tranquility.ai/guides/securitycompliance" target="_blank" className="flex flex-row items-center justify-left gap-2.5 mb-3 no-underline text-[#0F1D32] hover:text-brand-blue hover:cursor-pointer">
                        <ShieldCheckIcon size={24} weight="duotone" />
                        Security & Compliance
                      </a>
                    </li>
                    <li className="mb-3">
                      <a href="https://tranquility.ai/guides/timepilot-product-updates" target="_blank" className="flex flex-row items-center justify-left gap-2.5 mb-3 no-underline text-[#0F1D32] hover:text-brand-blue hover:cursor-pointer">
                        <NewspaperIcon size={24} weight="duotone" />
                        Product Updates
                      </a>
                    </li>
                    <li className="mb-3">
                      <button type="button" onClick={onFeedbackClick} className="flex flex-row items-center justify-left gap-3 hover:cursor-pointer hover:text-brand-blue">                          
                        <MegaphoneIcon size={22} weight="duotone" />
                        <span>Feedback</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>}
          <button id="LogoutButton" aria-label="Log out" onClick={event => {
          handleLogout();
          recordStat('LogoutButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
        }} className="icon-text-btn-vertical">
            <div className="icon-nav-btn"><SignOutIcon size={28} weight="light" /></div>
            <span className={`whitespace-nowrap ${overflowLevel ? '' : ''}`}>Log Out</span>
          </button>
          <Popover showArrow={true} placement="bottom-end" classNames={{
          content: 'flex flex-col justify-start items-start bg-white text-black max-w-[400px] p-4 rounded-md'
        }} onOpenChange={open => setIsPopoverVisible(open)}>
            <PopoverTrigger className="mt-1">
              <button type="button" aria-label="CJIS Compliance Information" aria-haspopup="dialog" className="bg-transparent border-none p-0 hover:cursor-help">
                <Image src="/cjis.png" alt="CJIS logo" className={`${isPopoverVisible ? 'grayscale-0 opacity-100' : 'grayscale opacity-70'} hover:grayscale-0 hover:opacity-100`} priority width={76} height={50} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="h-fit bg-white">
              <div className="flex flex-row gap-2 pb-4 border-double border-black border-b-[3px]">
                <Image src="/cjis.png" alt="CJIS logo" className={`${isPopoverVisible ? 'grayscale-0 opacity-100' : 'grayscale opacity-70'} hover:grayscale-0 hover:opacity-100 hover:cursor-help`} priority width={128} height={85.25} />
                <h3 className="text-[22px] font-normal text-[#0F1D32]">CJIS-Compliant Infrastructure</h3>
              </div>
              <p className="text-[18px] italic leading-normal text-[#0F1D32]">
                TimePilot ensures the highest level of data security and is fully CJIS Compliant.
              </p>
              <ul className="list-disc m-0 ml-8 p-0 text-[#0F1D32] text-[14px]">
                <li className="mb-4">Meets or exceeds FBI Security Management Control standards</li>
                <li>Complete audit trail of all system access and changes</li>
              </ul>
            </PopoverContent>
          </Popover>
        </div>
      </div>}

      <HotkeysModal isOpen={showHotkeysModal} onOpenChange={setShowHotkeysModal} data-sentry-element="HotkeysModal" data-sentry-source-file="navigation-bar-horizontal.tsx" />
      <HotKeys onShowHotkeys={() => setShowHotkeysModal(true)} data-sentry-element="HotKeys" data-sentry-source-file="navigation-bar-horizontal.tsx" />

      {overflowLevel > 0 && <div className="pr-[5px]">
          <Dropdown isOpen={isOpen} onOpenChange={open => {
        setIsOpen(open);
        if (open) {
          recordStat('OverflowMenuButtonHovered', new MouseEvent('hover') as unknown as React.MouseEvent, statsContext, !!featureFlags?.useIntercom);
        }
      }} classNames={{
        base: '',
        content: `z-[50] min-w-[100px] p-3 bg-[#59A2F7] rounded-[0.125rem] ${overflowLevel < 5 ? 'mt-[-3px]' : 'mt-[4px]'}`
      }}>
            <DropdownTrigger>
              <button aria-label="Customise options" className="mt-1 mr-1.5 sm:mr-2" id="OverflowMenuButton" onClick={event => {
            recordStat('OverflowMenuButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
          }}>
                <DotsThreeOutlineIcon size={overflowLevel < 5 ? 32 : 24} />
              </button>
            </DropdownTrigger>
            <DropdownMenu classNames={{
          base: 'p-0',
          list: 'p-0 gap-0'
        }} aria-label="Overflow menu">
              {overflowMenuItems.map((item, index) => <DropdownItem id={`OverflowMenuButton-${index}`} key={index} classNames={{
            base: 'py-1'
          }} onPress={event => {
            const menuItemKey = Object.entries(MenuItems).find(([, el]) => el === item)?.[0] ?? 'Unknown';
            recordStat(`OverflowMenuButton${menuItemKey}Clicked`, event as unknown as React.MouseEvent, statsContext, !!featureFlags?.useIntercom);
          }}>
                  {item}
                </DropdownItem>)}
            </DropdownMenu>
          </Dropdown>
        </div>}
    </nav>;
};
export default NavigationBarHorizontal;